import { z } from "zod";
import { PlatformReturnToSchema } from "../auth/index.js";
import { pageOf, PageQuerySchema } from "../common/pagination.js";
import { DateOnlySchema, IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
import { PlanCodeSchema, SubscriptionStatusSchema } from "../billing/index.js";
import { BILLING_INVOICE_PURPOSES } from "../billing/subscription.js";
import { MembershipStatusSchema } from "../members/index.js";
import { OrganizationStatusSchema } from "../organizations/index.js";
import { QueueNameSchema } from "../queues/index.js";
const MinorSchema = z.string().regex(/^-?(0|[1-9]\d{0,17})$/);
const ReasonSchema = z.string().trim().min(10).max(1e3);
const PlatformStepUpQuerySchema = z.strictObject({ returnTo: PlatformReturnToSchema });
const PlatformLoginQuerySchema = PlatformStepUpQuerySchema.extend({
  /** Authenticate again now (max_age=0): after a sign-in that only registered the key. */
  fresh: z.literal("1").optional()
});
const PlatformMeSchema = z.strictObject({
  admin: z.strictObject({
    id: IdSchema,
    email: z.string(),
    displayName: z.string(),
    /** Whether the admin has a support identity (needed to receive a break-glass access). */
    hasSupportIdentity: z.boolean()
  }),
  /** The value to send back in X-CSRF-Token (also in the __Host-bf_admin_csrf cookie). */
  csrfToken: z.string(),
  steppedUpAt: IsoDateTimeSchema.nullable(),
  sessionExpiresAt: IsoDateTimeSchema
});
const PlatformLogoutResponseSchema = z.strictObject({ endSessionUrl: z.url() });
const PlatformOrganizationListQuerySchema = PageQuerySchema.extend({
  q: z.string().trim().min(1).max(100).optional(),
  status: OrganizationStatusSchema.optional()
});
const PlatformOrganizationSummarySchema = z.strictObject({
  id: IdSchema,
  legalName: z.string(),
  tradeName: z.string().nullable(),
  taxId: z.string(),
  governorate: z.string().nullable(),
  status: OrganizationStatusSchema,
  createdAt: IsoDateTimeSchema,
  planCode: PlanCodeSchema.nullable(),
  subscriptionStatus: SubscriptionStatusSchema.nullable(),
  /** Active members taking a seat. */
  members: z.int().min(0),
  storageBytes: MinorSchema,
  dueAt: IsoDateTimeSchema.nullable()
});
const PlatformOrganizationListSchema = pageOf(PlatformOrganizationSummarySchema);
const PlatformOrgParamsSchema = z.strictObject({ orgId: IdSchema });
const PLATFORM_DELETION_STATUSES = ["pending", "approved", "rejected", "cancelled"];
const PlatformDeletionStatusSchema = z.enum(PLATFORM_DELETION_STATUSES);
const DeletionAdminRefSchema = z.strictObject({ id: IdSchema, displayName: z.string() });
const PlatformDeletionRequestSchema = z.strictObject({
  id: IdSchema,
  organization: z.strictObject({
    id: IdSchema,
    legalName: z.string(),
    status: OrganizationStatusSchema
  }),
  ticketRef: z.string(),
  reason: z.string(),
  status: PlatformDeletionStatusSchema,
  previousStatus: OrganizationStatusSchema,
  requestedBy: DeletionAdminRefSchema,
  requestedAt: IsoDateTimeSchema,
  decidedBy: DeletionAdminRefSchema.nullable(),
  decidedAt: IsoDateTimeSchema.nullable(),
  decisionReason: z.string().nullable(),
  /** The deletion itself, once approved (scheduled → running → done). */
  execution: z.strictObject({
    status: z.enum(["scheduled", "running", "cancelled", "done"]),
    executedAt: IsoDateTimeSchema.nullable()
  }).nullable()
});
const PlatformOrganizationDetailSchema = z.strictObject({
  organization: PlatformOrganizationSummarySchema,
  owners: z.array(z.strictObject({ displayName: z.string(), email: z.string() })).max(10),
  subscription: z.strictObject({
    status: SubscriptionStatusSchema,
    planCode: PlanCodeSchema,
    cycle: z.enum(["monthly", "annual"]),
    extraSeats: z.int().min(0),
    trialEndsAt: IsoDateTimeSchema.nullable(),
    currentPeriod: z.strictObject({ start: IsoDateTimeSchema, end: IsoDateTimeSchema }),
    dueAt: IsoDateTimeSchema.nullable(),
    cancelAt: IsoDateTimeSchema.nullable(),
    pausedUntil: IsoDateTimeSchema.nullable(),
    paymentMethod: z.string().nullable(),
    version: z.int().min(1)
  }).nullable(),
  usage: z.strictObject({
    seats: z.strictObject({ used: z.int().min(0), billable: z.int().min(0).nullable() }),
    storageBytes: MinorSchema,
    /** Latest monthly usage records (metric → quantity). */
    month: z.record(z.string(), MinorSchema)
  }),
  invoices: z.array(
    z.strictObject({
      id: IdSchema,
      number: z.string(),
      purpose: z.enum(BILLING_INVOICE_PURPOSES),
      issueDate: DateOnlySchema,
      dueDate: DateOnlySchema,
      totalTtcMinor: MinorSchema,
      status: z.enum(["open", "paid", "void"]),
      paidAt: IsoDateTimeSchema.nullable()
    })
  ).max(50),
  dunning: z.array(
    z.strictObject({
      stage: z.string(),
      dueAt: IsoDateTimeSchema,
      occurredAt: IsoDateTimeSchema
    })
  ).max(50),
  deletion: z.strictObject({
    reason: z.string(),
    scheduledFor: IsoDateTimeSchema,
    status: z.enum(["scheduled", "running", "cancelled", "done"])
  }).nullable(),
  /** The latest four-eyes deletion request of the back-office (pending or decided). */
  platformDeletion: PlatformDeletionRequestSchema.nullable()
});
const SubscriptionOverrideRequestSchema = z.strictObject({
  status: SubscriptionStatusSchema,
  reason: ReasonSchema
});
const ManualPaymentRequestSchema = z.strictObject({
  billingInvoiceId: IdSchema,
  method: z.enum(["transfer", "cheque"]),
  receivedOn: DateOnlySchema,
  reference: z.string().trim().min(1).max(200),
  cheque: z.strictObject({
    number: z.string().trim().min(1).max(40),
    bank: z.string().trim().min(1).max(100)
  }).optional()
});
const ManualPaymentResultSchema = z.strictObject({
  number: z.string(),
  amountMinor: MinorSchema
});
const PlatformDeadLetterJobSchema = z.strictObject({
  id: z.string().min(1).max(200),
  queue: QueueNameSchema,
  eventType: z.string(),
  tenantId: IdSchema,
  attempts: z.int().min(0),
  failedAt: IsoDateTimeSchema,
  /** The error message (never a stack or the payload). */
  error: z.string().max(1e3)
});
const PlatformDeadLetterListSchema = z.strictObject({
  items: z.array(PlatformDeadLetterJobSchema).max(200)
});
const PlatformJobParamsSchema = z.strictObject({
  jobId: z.string().min(1).max(200).regex(/^[A-Za-z0-9:_-]+$/)
});
const PlatformSecuritySignalsQuerySchema = z.strictObject({
  days: z.coerce.number().int().min(1).max(90).default(7)
});
const PlatformSecuritySignalsSchema = z.strictObject({
  from: IsoDateTimeSchema,
  to: IsoDateTimeSchema,
  /** Failures per action (sign-in failures, step-up failures, lockouts…). */
  failures: z.array(
    z.strictObject({
      action: z.string(),
      realm: z.enum(["batiflow", "batiflow-platform"]),
      count: z.int().min(0)
    })
  ).max(100),
  /** Failures per day, for the chart. */
  perDay: z.array(z.strictObject({ day: DateOnlySchema, failures: z.int().min(0) })).max(91),
  /** Subjects with the most failures (Keycloak ids, no personal data). */
  topSubjects: z.array(z.strictObject({ subject: z.string(), failures: z.int().min(1) })).max(20)
});
const PlatformAuditQuerySchema = PageQuerySchema.extend({ orgId: IdSchema.optional() });
const PlatformAuditEventSchema = z.strictObject({
  id: IdSchema,
  occurredAt: IsoDateTimeSchema,
  admin: z.strictObject({ id: IdSchema, displayName: z.string() }),
  action: z.string(),
  organizationId: IdSchema.nullable(),
  reason: z.string().nullable()
});
const PlatformAuditListSchema = pageOf(PlatformAuditEventSchema);
const BREAK_GLASS_STATUSES = [
  "pending",
  "active",
  "rejected",
  "cancelled",
  "expired",
  "revoked"
];
const BreakGlassStatusSchema = z.enum(BREAK_GLASS_STATUSES);
const BreakGlassBasisSchema = z.enum(["owner_consent", "legal_requisition"]);
const BreakGlassCreateRequestSchema = z.strictObject({
  organizationId: IdSchema,
  ticketRef: z.string().trim().min(1).max(100),
  reason: ReasonSchema,
  basis: BreakGlassBasisSchema,
  /** Where the owner's consent or the legal requisition is recorded. */
  basisRef: z.string().trim().min(1).max(200),
  /** 15 minutes to 4 hours. */
  durationMinutes: z.int().min(15).max(240)
});
const AdminRefSchema = z.strictObject({ id: IdSchema, displayName: z.string() });
const BreakGlassRequestSchema = z.strictObject({
  id: IdSchema,
  organization: z.strictObject({ id: IdSchema, legalName: z.string() }),
  ticketRef: z.string(),
  reason: z.string(),
  basis: BreakGlassBasisSchema,
  basisRef: z.string(),
  durationMinutes: z.int(),
  status: BreakGlassStatusSchema,
  requestedBy: AdminRefSchema,
  requestedAt: IsoDateTimeSchema,
  decidedBy: AdminRefSchema.nullable(),
  decidedAt: IsoDateTimeSchema.nullable(),
  startsAt: IsoDateTimeSchema.nullable(),
  expiresAt: IsoDateTimeSchema.nullable(),
  endedAt: IsoDateTimeSchema.nullable()
});
const BreakGlassListQuerySchema = z.strictObject({
  status: BreakGlassStatusSchema.optional()
});
const BreakGlassListSchema = z.strictObject({
  items: z.array(BreakGlassRequestSchema).max(200)
});
const BreakGlassParamsSchema = z.strictObject({ requestId: IdSchema });
const BreakGlassRejectRequestSchema = z.strictObject({ reason: ReasonSchema });
const PlatformDeletionCreateRequestSchema = z.strictObject({
  organizationId: IdSchema,
  ticketRef: z.string().trim().min(1).max(100),
  reason: ReasonSchema,
  confirmation: z.string().trim().min(1).max(300)
});
const PlatformDeletionListQuerySchema = z.strictObject({
  status: PlatformDeletionStatusSchema.optional()
});
const PlatformDeletionListSchema = z.strictObject({
  items: z.array(PlatformDeletionRequestSchema).max(200)
});
const PlatformDeletionParamsSchema = z.strictObject({ requestId: IdSchema });
const PlatformDeletionRejectRequestSchema = z.strictObject({ reason: ReasonSchema });
const PlatformAccountStatusSchema = z.enum(["active", "disabled"]);
const PlatformAccountSchema = z.strictObject({
  id: IdSchema,
  displayName: z.string(),
  email: z.string(),
  /** `disabled`: locked everywhere (Keycloak account disabled, every session ended). */
  status: PlatformAccountStatusSchema,
  isGuest: z.boolean(),
  /** The account was deleted (anonymised); nothing can be done with it any more. */
  deleted: z.boolean(),
  lastSeenAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema
});
const PlatformMemberSchema = z.strictObject({
  id: IdSchema,
  user: PlatformAccountSchema,
  roleKey: z.string(),
  status: MembershipStatusSchema,
  joinedAt: IsoDateTimeSchema.nullable(),
  suspendedAt: IsoDateTimeSchema.nullable(),
  deactivatedAt: IsoDateTimeSchema.nullable()
});
const PlatformMemberListQuerySchema = PageQuerySchema.extend({
  status: MembershipStatusSchema.optional()
});
const PlatformMemberListSchema = pageOf(PlatformMemberSchema);
const PlatformMemberParamsSchema = z.strictObject({
  orgId: IdSchema,
  membershipId: IdSchema
});
const PlatformAccountDetailSchema = z.strictObject({
  account: PlatformAccountSchema,
  memberships: z.array(
    z.strictObject({
      id: IdSchema,
      organization: z.strictObject({
        id: IdSchema,
        legalName: z.string(),
        status: OrganizationStatusSchema
      }),
      roleKey: z.string(),
      status: MembershipStatusSchema
    })
  ).max(200),
  /** Why the account cannot be deleted yet (null: it can). */
  deletionBlocker: z.enum(["owner", "support_identity"]).nullable()
});
const PlatformUserParamsSchema = z.strictObject({ userId: IdSchema });
const PlatformReasonRequestSchema = z.strictObject({ reason: ReasonSchema });
const PlatformAccountDeleteRequestSchema = z.strictObject({
  reason: ReasonSchema,
  confirmation: z.string().trim().min(1).max(320)
});
export {
  BREAK_GLASS_STATUSES,
  BreakGlassBasisSchema,
  BreakGlassCreateRequestSchema,
  BreakGlassListQuerySchema,
  BreakGlassListSchema,
  BreakGlassParamsSchema,
  BreakGlassRejectRequestSchema,
  BreakGlassRequestSchema,
  BreakGlassStatusSchema,
  ManualPaymentRequestSchema,
  ManualPaymentResultSchema,
  PLATFORM_DELETION_STATUSES,
  PlatformAccountDeleteRequestSchema,
  PlatformAccountDetailSchema,
  PlatformAccountSchema,
  PlatformAccountStatusSchema,
  PlatformAuditEventSchema,
  PlatformAuditListSchema,
  PlatformAuditQuerySchema,
  PlatformDeadLetterJobSchema,
  PlatformDeadLetterListSchema,
  PlatformDeletionCreateRequestSchema,
  PlatformDeletionListQuerySchema,
  PlatformDeletionListSchema,
  PlatformDeletionParamsSchema,
  PlatformDeletionRejectRequestSchema,
  PlatformDeletionRequestSchema,
  PlatformDeletionStatusSchema,
  PlatformJobParamsSchema,
  PlatformLoginQuerySchema,
  PlatformLogoutResponseSchema,
  PlatformMeSchema,
  PlatformMemberListQuerySchema,
  PlatformMemberListSchema,
  PlatformMemberParamsSchema,
  PlatformMemberSchema,
  PlatformOrgParamsSchema,
  PlatformOrganizationDetailSchema,
  PlatformOrganizationListQuerySchema,
  PlatformOrganizationListSchema,
  PlatformOrganizationSummarySchema,
  PlatformReasonRequestSchema,
  PlatformSecuritySignalsQuerySchema,
  PlatformSecuritySignalsSchema,
  PlatformStepUpQuerySchema,
  PlatformUserParamsSchema,
  SubscriptionOverrideRequestSchema
};
//# sourceMappingURL=index.js.map